import React from 'react';
import type { Log } from '../../types/log';

type ClinicMetadata = {
  source?: string;
  clinicLogId?: string;
  patientId?: string | null;
  patientName?: string | null;
  petName?: string | null;
  veterinarian?: string | null;
  timestamp?: string | null;
  subjective?: string | null;
  objective?: string | null;
  assessment?: string | null;
  plan?: string | null;
  vitals?: {
    temp?: number | null;
    hr?: number | null;
    rr?: number | null;
  } | null;
};

const asString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number') return String(value);
  return null;
};

const isClinicClinicalLog = (log: Log): boolean =>
  log.category === 'clinic_clinical_log';

const getMetadata = (log: Log): ClinicMetadata | null => {
  if (!log.metadata || typeof log.metadata !== 'object') return null;
  const meta = log.metadata as Record<string, unknown>;
  if (!('source' in meta) || meta.source !== 'clinic-erp') return null;
  return meta as unknown as ClinicMetadata;
};

/**
 * Resolve the pet name to display. Priority:
 * 1. metadata.petName  (set by the sync handler after joining with subjects)
 * 2. metadata.patientName  (legacy / fallback from clinic-erp payload)
 */
const resolvePetName = (meta: ClinicMetadata): string | null =>
  asString(meta.petName) || asString(meta.patientName);

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-3 py-2 border-t first:border-t-0 dark:border-claude-darkBorder border-claude-border">
    <dt className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary uppercase tracking-wider">
      {label}
    </dt>
    <dd className="text-sm dark:text-claude-darkText text-claude-text whitespace-pre-wrap break-words">
      {value || <span className="dark:text-claude-darkTextSecondary text-claude-textSecondary italic">—</span>}
    </dd>
  </div>
);

interface ClinicLogDetailsProps {
  log: Log;
  compact?: boolean;
}

/**
 * Structured renderer for clinic_clinical_log entries synced from clinic-erp.
 * Reads log.metadata and renders each SOAP field + vitals as a labeled row.
 * Falls back gracefully if metadata is missing (e.g., legacy rows from before
 * the structured schema was introduced).
 */
export const ClinicLogDetails: React.FC<ClinicLogDetailsProps> = ({ log, compact = false }) => {
  if (!isClinicClinicalLog(log)) return null;
  const meta = getMetadata(log);
  if (!meta) return null;

  const vitals: string[] = [];
  if (meta.vitals?.temp !== null && meta.vitals?.temp !== undefined) vitals.push(`Temp ${meta.vitals.temp}`);
  if (meta.vitals?.hr !== null && meta.vitals?.hr !== undefined) vitals.push(`HR ${meta.vitals.hr}`);
  if (meta.vitals?.rr !== null && meta.vitals?.rr !== undefined) vitals.push(`RR ${meta.vitals.rr}`);

  if (compact) {
    const petName = resolvePetName(meta);
    return (
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
        {petName && (
          <span className="font-medium dark:text-claude-darkText text-claude-text">{petName}</span>
        )}
        {vitals.length > 0 && <span>{vitals.join(' · ')}</span>}
        {asString(meta.assessment) && (
          <span className="line-clamp-1">
            Assessment: <span className="dark:text-claude-darkText text-claude-text">{asString(meta.assessment)}</span>
          </span>
        )}
      </div>
    );
  }

  const petName = resolvePetName(meta);
  return (
    <section className="rounded-lg border dark:border-claude-darkBorder border-claude-border overflow-hidden">
      <header className="px-3 py-2 flex items-center justify-between dark:bg-claude-darkSurface bg-claude-surface">
        <h4 className="text-sm font-semibold dark:text-claude-darkText text-claude-text">
          Clinical Record
        </h4>
        {asString(meta.source) && (
          <span className="text-[10px] uppercase tracking-wider dark:text-claude-darkTextSecondary text-claude-textSecondary">
            Source {asString(meta.source)}
          </span>
        )}
      </header>
      <dl className="px-3">
        <Row label="Pet Name" value={petName} />
        <Row label="Veterinarian" value={asString(meta.veterinarian)} />
        <Row label="Time" value={asString(meta.timestamp)} />
        {vitals.length > 0 && <Row label="Vitals" value={vitals.join(' · ')} />}
        <Row label="Subjective" value={asString(meta.subjective)} />
        <Row label="Objective" value={asString(meta.objective)} />
        <Row label="Assessment" value={asString(meta.assessment)} />
        <Row label="Plan" value={asString(meta.plan)} />
      </dl>
    </section>
  );
};

export default ClinicLogDetails;
